/**
 * TradeWindowModal.tsx — T-3 Trade Window minigame (R16 Builder-BY)
 *
 * Three-phase card-swap negotiation between the drawer and one chosen opponent.
 * Triggered by clicking "Offer Card Swap" on TradingPrompt (one-shot per game
 * per drawer).
 *
 * Flow:
 *   1. SELECT   — Drawer sees own hand + opponent's FULL hand. Picks 2 of their
 *                 own cards to offer and 2 of the opponent's to request. Also
 *                 selects which opponent.
 *   2. RESPOND  — 3s privacy beat (look-away overlay). Then responder sees only
 *                 the 4 cards involved + Accept / Reject buttons.
 *   3. RESOLVED — Result banner (accepted / rejected + penalty note). Continue.
 *
 * INVARIANTS:
 *   - Returns null when boardAnimationActive === true (R11.2 gate).
 *   - Returns null when pendingTrade === null.
 *   - No global aura side-effects — NO bySeat[-1].
 *   - Card art / data NEVER modified (IMMUTABLE rule). Only CardInHand id+instanceId
 *     pairs are swapped across hands[] arrays.
 *
 * VISIBLE WHEN (phase=select):  pendingTrade !== null AND phase="select"  AND boardAnimationActive===false
 * TEST PATH (phase=select):     land on Trading tile → click trader → "Offer Card Swap" → TradeWindowModal renders.
 *
 * VISIBLE WHEN (phase=respond): pendingTrade !== null AND phase="respond" AND boardAnimationActive===false
 * TEST PATH (phase=respond):    select phase → commit selection → privacy beat completes → respond phase.
 *
 * VISIBLE WHEN (phase=resolved): pendingTrade !== null AND phase="resolved" AND boardAnimationActive===false
 * TEST PATH (phase=resolved):    respond phase → Accept or Reject → resolved banner.
 */

import type React from "react";
import { useEffect, useRef, useState } from "react";
import {
  type CardInHand,
  SEAT_COLORS,
  type SeatIndex,
  TRADE_REJECT_PENALTY,
  getCardById,
  useGameStore,
} from "../../store/game-store.js";
import styles from "./TradeWindowModal.module.css";

const PRIVACY_BEAT_MS = 3_000; // 3s look-away overlay (mirrors PolicyAuctionModal)

function seatLabel(seat: SeatIndex): string {
  return `P${seat + 1}`;
}

function cardTypeCls(cardType: string | undefined): string {
  if (cardType === "Policy") return styles.cardTypePolicy ?? "";
  if (cardType === "Mitigate") return styles.cardTypeMitigate ?? "";
  if (cardType === "Tech") return styles.cardTypeTech ?? "";
  return styles.cardTypeEvent ?? "";
}

/** Renders a single card slot; highlighted when selected. */
function CardSlot({
  card,
  selected,
  disabled,
  onToggle,
}: {
  card: CardInHand;
  selected: boolean;
  disabled: boolean;
  onToggle: (instanceId: string) => void;
}): React.ReactElement {
  const def = getCardById(card.id);
  return (
    <button
      type="button"
      className={`${styles.cardSlot} ${selected ? styles.cardSlotSelected : ""} ${disabled && !selected ? styles.cardSlotDisabled : ""}`}
      onClick={() => !disabled && onToggle(card.instanceId)}
      data-testid={`trade-card-${card.instanceId}`}
      aria-pressed={selected}
    >
      <div className={styles.cardName}>{def?.cardName ?? card.id}</div>
      {def?.cardType !== undefined && (
        <span className={`${styles.cardType} ${cardTypeCls(def.cardType)}`}>{def.cardType}</span>
      )}
    </button>
  );
}

export function TradeWindowModal(): React.ReactElement | null {
  const pendingTrade = useGameStore((s) => s.pendingTrade);
  const boardAnimationActive = useGameStore((s) => s.boardAnimationActive);
  const hands = useGameStore((s) => s.hands);
  const tokens = useGameStore((s) => s.tokens);
  const lostSeats = useGameStore((s) => s.lostSeats);
  const numPlayers = useGameStore((s) => s.numPlayers);
  const commitTradeOffer = useGameStore((s) => s.commitTradeOffer);
  const acceptTrade = useGameStore((s) => s.acceptTrade);
  const rejectTrade = useGameStore((s) => s.rejectTrade);
  const dismissTrade = useGameStore((s) => s.dismissTrade);

  // ── SELECT phase local state ───────────────────────────────────
  // Drawer's 2 selected cards (own hand)
  const [selectedOffered, setSelectedOffered] = useState<string[]>([]);
  // Chosen responder seat
  const [chosenOpponent, setChosenOpponent] = useState<SeatIndex | null>(null);
  // Responder's 2 selected cards
  const [selectedRequested, setSelectedRequested] = useState<string[]>([]);

  // ── RESPOND phase: privacy beat ────────────────────────────────
  const [privacyVisible, setPrivacyVisible] = useState(false);
  const privacyDismissedRef = useRef(false);

  // Fire privacy beat when phase transitions to "respond"
  useEffect(() => {
    if (pendingTrade?.phase !== "respond") {
      privacyDismissedRef.current = false;
      return;
    }
    if (privacyDismissedRef.current) return;
    setPrivacyVisible(true);
    const t = window.setTimeout(() => {
      setPrivacyVisible(false);
      privacyDismissedRef.current = true;
    }, PRIVACY_BEAT_MS);
    return () => window.clearTimeout(t);
  }, [pendingTrade?.phase]);

  // Reset local selection when modal opens fresh
  useEffect(() => {
    if (pendingTrade === null) {
      setSelectedOffered([]);
      setChosenOpponent(null);
      setSelectedRequested([]);
    }
  }, [pendingTrade]);

  // Hard gates
  if (pendingTrade === null || boardAnimationActive) return null;

  const { offerSeat, responderSeat, offeredInstanceIds, requestedInstanceIds, phase, decision } =
    pendingTrade;

  // ────────────────────────────────────────────────────────────────
  // RESOLVED PHASE
  // ────────────────────────────────────────────────────────────────
  if (phase === "resolved") {
    const accepted = decision === "accept";
    const offerName = seatLabel(offerSeat);
    const respName = responderSeat !== null ? seatLabel(responderSeat) : "—";
    return (
      <div
        className={styles.overlay}
        // biome-ignore lint/a11y/useSemanticElements: custom overlay needed for z-index layering
        role="dialog"
        aria-modal="true"
        aria-label="Trade Result"
        data-testid="trade-window-modal"
        data-phase="resolved"
      >
        <div className={styles.panel}>
          <div className={styles.header}>
            <div>
              <div className={styles.title}>CARD SWAP</div>
              <div className={styles.subtitle}>
                {offerName} ↔ {respName}
              </div>
            </div>
            <div className={styles.phaseChip}>RESOLVED</div>
          </div>

          <div
            className={`${styles.resultBanner} ${accepted ? styles.resultBannerAccept : styles.resultBannerReject}`}
          >
            <div
              className={`${styles.resultTitle} ${accepted ? styles.resultTitleAccept : styles.resultTitleReject}`}
            >
              {accepted ? "TRADE ACCEPTED" : "TRADE REJECTED"}
            </div>
            {accepted ? (
              <div className={styles.resultDetail}>Cards swapped successfully.</div>
            ) : (
              <div className={styles.resultDetail}>
                {offerName} loses {TRADE_REJECT_PENALTY}T as reject penalty.
              </div>
            )}
          </div>

          <button
            type="button"
            className={styles.btnClose}
            onClick={dismissTrade}
            data-testid="trade-dismiss-btn"
          >
            CONTINUE
          </button>
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────
  // RESPOND PHASE
  // ────────────────────────────────────────────────────────────────
  if (phase === "respond") {
    if (responderSeat === null || offeredInstanceIds === null || requestedInstanceIds === null) {
      return null; // defensive
    }
    const drawerHand = hands[offerSeat] ?? [];
    const respHand = hands[responderSeat] ?? [];
    // Responder sees only the 4 cards involved (privacy: no full hand).
    const shownOffered = drawerHand.filter((c) =>
      offeredInstanceIds.some((id) => id === c.instanceId),
    );
    const shownRequested = respHand.filter((c) =>
      requestedInstanceIds.some((id) => id === c.instanceId),
    );
    const responderTokens = tokens[responderSeat] ?? 0;

    return (
      <div
        className={styles.overlay}
        // biome-ignore lint/a11y/useSemanticElements: custom overlay needed for z-index layering
        role="dialog"
        aria-modal="true"
        aria-label="Trade Window — Respond"
        data-testid="trade-window-modal"
        data-phase="respond"
      >
        <div className={styles.panel}>
          <div className={styles.panelInner}>
            {/* Privacy beat — 3s look-away overlay (mirrors PolicyAuctionModal lines 251-261) */}
            {privacyVisible && (
              <div className={styles.privacyOverlay} data-testid="trade-privacy-overlay">
                <div className={styles.privacyTitle}>CARD SWAP OFFER</div>
                <div className={styles.privacySubtitle}>
                  {seatLabel(responderSeat)} — please review the trade
                </div>
                <div className={styles.privacySubtitle} style={{ marginTop: 8 }}>
                  Other players, please look away
                </div>
              </div>
            )}

            <div className={styles.header}>
              <div>
                <div className={styles.title}>CARD SWAP</div>
                <div className={styles.subtitle}>
                  {seatLabel(offerSeat)} offers → {seatLabel(responderSeat)} responds
                </div>
              </div>
              <div className={styles.phaseChip}>RESPOND</div>
            </div>

            <div className={styles.instructionBanner} data-testid="trade-respond-instruction">
              <span
                className={styles.seatChip}
                style={{
                  background: `${SEAT_COLORS[responderSeat] ?? "#888"}33`,
                  borderColor: SEAT_COLORS[responderSeat] ?? "#888",
                  color: SEAT_COLORS[responderSeat] ?? "#fff",
                }}
              >
                {seatLabel(responderSeat)}
              </span>
              <span>
                — do you accept this swap? Rejecting costs {seatLabel(offerSeat)}{" "}
                {TRADE_REJECT_PENALTY}T.
              </span>
            </div>

            {/* Cards offered BY drawer */}
            <div>
              <div className={styles.sectionLabel}>{seatLabel(offerSeat)} OFFERS YOU</div>
              <div className={styles.cardGrid} data-testid="trade-offered-cards">
                {shownOffered.map((c) => (
                  <CardSlot
                    key={c.instanceId}
                    card={c}
                    selected={false}
                    disabled={true}
                    onToggle={() => {}}
                  />
                ))}
              </div>
            </div>

            <div className={styles.divider} />

            {/* Cards requested FROM responder */}
            <div>
              <div className={styles.sectionLabel}>{seatLabel(offerSeat)} WANTS FROM YOU</div>
              <div className={styles.cardGrid} data-testid="trade-requested-cards">
                {shownRequested.map((c) => (
                  <CardSlot
                    key={c.instanceId}
                    card={c}
                    selected={false}
                    disabled={true}
                    onToggle={() => {}}
                  />
                ))}
              </div>
            </div>

            <div className={styles.btnRow}>
              <button
                type="button"
                className={styles.btnAccept}
                onClick={acceptTrade}
                data-testid="trade-accept-btn"
              >
                ACCEPT
              </button>
              <button
                type="button"
                className={styles.btnReject}
                onClick={rejectTrade}
                data-testid="trade-reject-btn"
              >
                REJECT (−{TRADE_REJECT_PENALTY}T to {seatLabel(offerSeat)})
              </button>
            </div>
            <div className={styles.penaltyNote}>
              Balance: {seatLabel(responderSeat)} {responderTokens}T
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────
  // SELECT PHASE
  // ────────────────────────────────────────────────────────────────
  const drawerHand = hands[offerSeat] ?? [];
  const drawerTokens = tokens[offerSeat] ?? 0;

  // R20-GE-8: lowered minimum to 1 — eligible opponents need hand>=1.
  // Present seats only (opp < numPlayers) — absent seats are not in the game.
  const eligibleOpponents = ([0, 1, 2, 3] as SeatIndex[]).filter(
    (opp) =>
      opp !== offerSeat && opp < numPlayers && !lostSeats.has(opp) && (hands[opp]?.length ?? 0) >= 1,
  );

  const opponentHand: CardInHand[] = chosenOpponent !== null ? (hands[chosenOpponent] ?? []) : [];

  // R20-GE-8: variable trade size — clamp each side to max 2 and min 1.
  // Both sides must pick the SAME count (symmetric swap).
  const maxOffered = Math.min(2, drawerHand.length);
  const maxRequested = Math.min(2, opponentHand.length);

  function toggleOffered(instanceId: string): void {
    setSelectedOffered((prev) => {
      if (prev.includes(instanceId)) return prev.filter((id) => id !== instanceId);
      if (prev.length >= maxOffered) return prev;
      return [...prev, instanceId];
    });
  }

  function toggleRequested(instanceId: string): void {
    setSelectedRequested((prev) => {
      if (prev.includes(instanceId)) return prev.filter((id) => id !== instanceId);
      if (prev.length >= maxRequested) return prev;
      return [...prev, instanceId];
    });
  }

  function handleOpponentSelect(opp: SeatIndex): void {
    setChosenOpponent(opp);
    setSelectedRequested([]); // reset requested when opponent changes
  }

  // R20-GE-8: valid when both sides have >=1 card selected and equal counts.
  const canCommit =
    selectedOffered.length >= 1 &&
    chosenOpponent !== null &&
    selectedRequested.length >= 1 &&
    selectedOffered.length === selectedRequested.length;

  function handleCommit(): void {
    if (!canCommit || chosenOpponent === null) return;
    // R20-GE-8: pad to [string, string] — 1-card swaps pad with empty string sentinel,
    // commitTradeOffer validates against actual hand contents (empty sentinel won't match).
    // For 1-card trades, we pad the second slot; store validates only present cards.
    const offered: [string, string] =
      selectedOffered.length >= 2
        ? [selectedOffered[0]!, selectedOffered[1]!]
        : [selectedOffered[0]!, selectedOffered[0]!]; // 1-card: same id twice signals 1-swap
    const requested: [string, string] =
      selectedRequested.length >= 2
        ? [selectedRequested[0]!, selectedRequested[1]!]
        : [selectedRequested[0]!, selectedRequested[0]!];
    commitTradeOffer(offered, chosenOpponent, requested);
  }

  return (
    <div
      className={styles.overlay}
      // biome-ignore lint/a11y/useSemanticElements: custom overlay needed for z-index layering
      role="dialog"
      aria-modal="true"
      aria-label="Trade Window — Select"
      data-testid="trade-window-modal"
      data-phase="select"
    >
      <div className={styles.panel}>
        <div className={styles.panelInner}>
          <div className={styles.header}>
            <div>
              <div className={styles.title}>CARD SWAP</div>
              <div className={styles.subtitle}>
                {seatLabel(offerSeat)} — pick up to {maxOffered} to offer · pick up to{" "}
                {maxRequested} to request
              </div>
            </div>
            <div className={styles.phaseChip}>SELECT</div>
          </div>

          <div className={styles.instructionBanner} data-testid="trade-select-instruction">
            <span
              className={styles.seatChip}
              style={{
                background: `${SEAT_COLORS[offerSeat] ?? "#888"}33`,
                borderColor: SEAT_COLORS[offerSeat] ?? "#888",
                color: SEAT_COLORS[offerSeat] ?? "#fff",
              }}
            >
              {seatLabel(offerSeat)}
            </span>
            <span>
              — Select 1–{maxOffered} of your cards to offer (equal count from opponent). Balance:{" "}
              {drawerTokens}T
            </span>
          </div>

          {/* Drawer's own hand — select 2 to offer */}
          <div>
            <div className={styles.sectionLabel}>
              YOUR CARDS — pick 1–{maxOffered} to offer ({selectedOffered.length}/{maxOffered})
            </div>
            <div className={styles.cardGrid} data-testid="trade-own-hand">
              {drawerHand.map((c) => (
                <CardSlot
                  key={c.instanceId}
                  card={c}
                  selected={selectedOffered.includes(c.instanceId)}
                  disabled={!selectedOffered.includes(c.instanceId) && selectedOffered.length >= 2}
                  onToggle={toggleOffered}
                />
              ))}
            </div>
          </div>

          <div className={styles.divider} />

          {/* Opponent selector */}
          <div>
            <div className={styles.sectionLabel}>CHOOSE OPPONENT</div>
            <div className={styles.opponentRow} data-testid="trade-opponent-row">
              {eligibleOpponents.map((opp) => (
                <button
                  key={opp}
                  type="button"
                  className={`${styles.opponentBtn} ${chosenOpponent === opp ? styles.opponentBtnSelected : ""}`}
                  onClick={() => handleOpponentSelect(opp)}
                  style={{
                    borderColor:
                      chosenOpponent === opp ? (SEAT_COLORS[opp] ?? "#e8c547") : undefined,
                  }}
                  data-testid={`trade-opponent-btn-${opp}`}
                >
                  {seatLabel(opp)} ({hands[opp]?.length ?? 0} cards)
                </button>
              ))}
            </div>
          </div>

          {/* Opponent's hand — revealed in full to drawer (spec §2 "informed swap") */}
          {chosenOpponent !== null && (
            <div>
              <div className={styles.sectionLabel}>
                {seatLabel(chosenOpponent)}'S HAND — pick 1–{maxRequested} to request (
                {selectedRequested.length}/{maxRequested})
              </div>
              <div className={styles.cardGrid} data-testid="trade-opponent-hand">
                {opponentHand.map((c) => (
                  <CardSlot
                    key={c.instanceId}
                    card={c}
                    selected={selectedRequested.includes(c.instanceId)}
                    disabled={
                      !selectedRequested.includes(c.instanceId) && selectedRequested.length >= 2
                    }
                    onToggle={toggleRequested}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Preview of the deal */}
          {canCommit && chosenOpponent !== null && (
            <div className={styles.previewRow} data-testid="trade-preview">
              <div className={styles.previewBlock}>
                <div className={styles.previewLabel}>{seatLabel(offerSeat)} GIVES</div>
                <div className={styles.previewCardNames}>
                  {selectedOffered
                    .map((id) => {
                      const c = drawerHand.find((x) => x.instanceId === id);
                      return c ? (getCardById(c.id)?.cardName ?? c.id) : id;
                    })
                    .join(" + ")}
                </div>
              </div>
              <div className={styles.previewArrow}>⇄</div>
              <div className={styles.previewBlock}>
                <div className={styles.previewLabel}>{seatLabel(chosenOpponent)} GIVES</div>
                <div className={styles.previewCardNames}>
                  {selectedRequested
                    .map((id) => {
                      const c = opponentHand.find((x) => x.instanceId === id);
                      return c ? (getCardById(c.id)?.cardName ?? c.id) : id;
                    })
                    .join(" + ")}
                </div>
              </div>
            </div>
          )}

          <div className={styles.btnRow}>
            <button
              type="button"
              className={styles.btnConfirm}
              disabled={!canCommit}
              onClick={handleCommit}
              data-testid="trade-commit-btn"
            >
              SEND OFFER
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
